import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  User, 
  Calendar, 
  FileText, 
  Settings, 
  Shield, 
  Bell, 
  Download,
  Trash2,
  ChevronRight,
  BookOpen
} from 'lucide-react';
import { useFrontoffWorkingMode } from '../hooks/useSystemStatus';

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: string | number;
  description?: string;
}

export function AccountSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { features } = useFrontoffWorkingMode();

  const sidebarItems: SidebarItem[] = [
    {
      id: 'dashboard',
      label: t('auth.account.sidebar.dashboard'),
      icon: User,
      href: '/account',
      description: t('auth.account.sidebar.dashboardDesc')
    },
    {
      id: 'profile',
      label: t('auth.account.sidebar.profile'),
      icon: User,
      href: '/account/profile',
      description: t('auth.account.sidebar.profileDesc')
    },
    {
      id: 'appointments',
      label: t('auth.account.sidebar.appointments'),
      icon: Calendar,
      href: '/account/appointments',
      description: t('auth.account.sidebar.appointmentsDesc')
    },
    {
      id: 'documents',
      label: t('auth.account.sidebar.documents'),
      icon: FileText,
      href: '/account/documents',
      description: t('auth.account.sidebar.documentsDesc')
    },
    {
      id: 'preferences',
      label: t('auth.account.sidebar.preferences'),
      icon: Settings,
      href: '/account/preferences',
      description: t('auth.account.sidebar.preferencesDesc')
    },
    {
      id: 'security',
      label: t('auth.account.sidebar.security'),
      icon: Shield,
      href: '/account/security',
      description: t('auth.account.sidebar.securityDesc')
    },
    {
      id: 'notifications',
      label: t('auth.account.sidebar.notifications'),
      icon: Bell,
      href: '/account/notifications',
      description: t('auth.account.sidebar.notificationsDesc')
    },
  ];

  const isActive = (href: string) => {
    if (href === '/account') {
      return location.pathname === '/account';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="w-72 bg-white shadow-sm border-r border-gray-200 h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-textPrimary">
          {t('auth.account.sidebar.title')}
        </h2>
        <p className="text-sm text-textSecondary mt-1">
          {t('auth.account.sidebar.subtitle')}
        </p>
      </div>

      {/* Navigation */}
      <nav className="p-4 flex-1 overflow-y-auto">
        <ul className="space-y-2">
          {sidebarItems.map((item) => {
            const active = isActive(item.href);
            return (
              <li key={item.id}>
                <button
                  onClick={() => navigate(item.href)}
                  className={`fo-account-nav-link w-full flex items-start gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    active
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-textSecondary hover:text-textPrimary hover:bg-gray-50'
                  }`}
                >
                  <item.icon 
                    className={`h-5 w-5 shrink-0 mt-0.5 ${
                      active ? 'text-primary-600' : 'text-textSecondary'
                    }`} 
                  />
                  <span className="flex-1 min-w-0 text-left leading-snug break-words hyphens-none">
                    {item.label}
                    {item.badge && (
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full align-middle ${
                        active 
                          ? 'bg-primary-100 text-primary-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <ChevronRight 
                    className={`h-4 w-4 shrink-0 mt-1 ${
                      active ? 'text-primary-600' : 'text-textSecondary'
                    }`} 
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="space-y-2">
          {features.userGuide && (
            <a
              href="/docs/manual/fr/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-start gap-2 px-3 py-2 text-sm text-textSecondary hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors duration-200"
            >
              <BookOpen className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0 text-left leading-snug break-words hyphens-none">Guide utilisateur</span>
            </a>
          )}

          <button
            onClick={() => navigate('/account/export')}
            className="w-full flex items-start gap-2 px-3 py-2 text-sm text-textSecondary hover:text-textPrimary hover:bg-gray-50 rounded-lg transition-colors duration-200"
          >
            <Download className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1 min-w-0 text-left leading-snug break-words hyphens-none">{t('auth.account.sidebar.exportData')}</span>
          </button>
          
          <button
            onClick={() => navigate('/account/delete')}
            className="w-full flex items-start gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
          >
            <Trash2 className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="flex-1 min-w-0 text-left leading-snug break-words hyphens-none">{t('auth.account.sidebar.deleteAccount')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountSidebar;
